import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRedo } from '@components/icons/redo';
import { IconRemove } from '@components/icons/remove';
import { IconTeam } from '@components/icons/team';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { disabledSectionStyle } from '@styles/theme';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import { signetGetToken, signetGetCosigners, signetRemoveCosigner } from './api';

type SignetHeadProps = {
  isConnected: boolean;
  isValid: boolean;
};
const SignetHeader = ({ isConnected, isValid }: SignetHeadProps) => {
  const { t } = useStore();
  const { showModal } = useModal();
  return (
    <>
      <Stack space={1} orientation="horizontal" justify={'space-between'}>
        <Stack space={2}>
          <Text font="heading2" text={t('company.settings.apps.signet')} />
          <Stack space={0} orientation="horizontal">
            {isConnected ? (
              <Tag
                variant="green"
                size="small"
                text={t('integrations.signet.settings.header.connected')}
              />
            ) : (
              <Tag
                variant="purple"
                size="small"
                text={t('integrations.signet.settings.header.notconnected')}
              />
            )}
          </Stack>
        </Stack>
        {isConnected && isValid ? (
          <Actions>
            <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
              <Menu.Item
                label={t('integrations.signet.settings.modal.reconnect')}
                icon={<IconRedo />}
                onSelect={() => {
                  showModal('INTEGRATION_SIGNET_CONNECT', { isEdit: true });
                }}
              />
            </Menu>
          </Actions>
        ) : null}
      </Stack>
      {isConnected && !isValid ? (
        <Card space={6} variant="orange">
          <Text
            color="orange200"
            font="regular16"
            text={t('integrations.signet.settings.credentials.expired')}
          />
        </Card>
      ) : null}
    </>
  );
};

const CosignersList = ({ isConnected, isValid }: SignetHeadProps) => {
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const { showModal } = useModal();

  const cosignersQuery = useQuery({
    queryKey: ['signet-cosigners'],
    queryFn: () => signetGetCosigners(),
  });
  const client = useQueryClient();

  if (cosignersQuery.isError) {
    return null;
  }
  if (cosignersQuery.isSuccess) {
    const cosigners = cosignersQuery.data;
    return (
      <Stack space={4}>
        <SignetHeader isConnected={isConnected} isValid={isValid} />
        {isValid ? (
          <Text font="regular16" text={t('integrations.signet.settings.cosigners.description')} />
        ) : (
          <Stack space={2} orientation="horizontal" justify="flex-end">
            <Button
              variant="primary"
              text={t('integrations.signet.settings.modal.connect')}
              onClick={() => showModal('INTEGRATION_SIGNET_CONNECT', { isEdit: true })}
            />
          </Stack>
        )}
        <Separator />

        <Stack
          style={!isValid ? disabledSectionStyle : undefined}
          space={0}
          orientation="horizontal"
          align={'center'}
          justify="space-between"
        >
          <Text font="bold16" text={t('integrations.signet.settings.cosigners')} />
          <Button
            variant="secondary"
            text={t('integrations.signet.settings.cosigners.add')}
            iconLeft={<IconAddPlus />}
            onClick={() => showModal('INTEGRATION_SIGNET_COSIGNER', {})}
          />
        </Stack>
        <Stack space={2}>
          {cosigners.length === 0 ? (
            <Card space={6} variant="gray">
              <Stack space={2} orientation="horizontal">
                <IconTeam size={24} />
                <Text font="regular16" text={t('integrations.signet.settings.cosigners.none')} />
              </Stack>
            </Card>
          ) : (
            cosigners.map((cosigner) => (
              <Card space={4} key={cosigner.id} style={!isValid ? disabledSectionStyle : undefined}>
                <CosignerContainer>
                  <Stack space={1}>
                    <Text font="bold14" text={cosigner.name} />
                    <Text font="regular14" text={cosigner.email} color="purple60" />
                  </Stack>
                  <Stack space={1}>
                    <Text font="regular14" text={cosigner.ssn} color="purple60" />
                  </Stack>
                  <Actions>
                    <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
                      <Menu.Item
                        label={t('integrations.signet.settings.cosigners.edit')}
                        icon={<IconEdit />}
                        onSelect={() => {
                          showModal('INTEGRATION_SIGNET_COSIGNER', { cosigner });
                        }}
                      />
                      <Menu.Item
                        label={t('integrations.signet.settings.cosigners.delete')}
                        icon={<IconRemove />}
                        variant="danger"
                        onSelect={() => {
                          signetRemoveCosigner(cosigner.id.toString()).then(() => {
                            showSuccess(t('integrations.signet.settings.cosigners.remove.success'));
                            client.resetQueries(['signet-cosigners']);
                          });
                        }}
                      />
                    </Menu>
                  </Actions>
                </CosignerContainer>
              </Card>
            ))
          )}
        </Stack>
      </Stack>
    );
  }
  return <Spinner />;
};

export const SignetSettingsPage = () => {
  const { t } = useStore();
  const { showModal } = useModal();

  const tokenQuery = useQuery({
    queryKey: ['signet-token'],
    queryFn: () => signetGetToken(),
  });
  if (tokenQuery.isError) {
    return null;
  }
  if (tokenQuery.isSuccess) {
    if (tokenQuery.data.ok) {
      return <CosignersList isConnected={tokenQuery.data.ok} isValid={tokenQuery.data.valid} />;
    }
    return (
      <Stack space={4}>
        <SignetHeader isConnected={tokenQuery.data.ok} isValid={tokenQuery.data.valid} />
        <Text font="regular16" text={t('integrations.signet.settings.description')} />
        <Separator />
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Button
            variant="primary"
            text={t('integrations.signet.settings.modal.connect')}
            onClick={() => showModal('INTEGRATION_SIGNET_CONNECT', { isEdit: false })}
          />
        </Stack>
      </Stack>
    );
  }
  return <Spinner />;
};

const CosignerContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: '3fr 2fr 1fr',
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
});
